import { SelectionChange, SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatSelectionListChange } from '@angular/material/list';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { ToastrService } from 'ngx-toastr';
import { DateHelper } from 'src/shared/helpers/date.helper';
import { VantaResultExportType } from '../enums/vanta-result-export-type';
import { ISearchParams } from '../interfaces/search-params';
import { ResultNode, SearchResultItem } from '../interfaces/search-result-item';
import { DataHelperService } from '../services/data-helper.service';
import { VantaShotResultComponent } from '../vanta-shot-result/vanta-shot-result.component';
import { ExportService } from './../export.service';
import { DataService } from './../services/data.service';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../material';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@evc/web-components';
import { InputComponent } from '@evc/web-components';
import { IconComponent } from '@evc/web-components';
import { ModalService, ConfirmModalComponent } from '@evc/web-components';
import { LoaderService } from '@evc/platform';
import { finalize } from 'rxjs/operators';


@Component({
    selector: 'app-data.appdata',
    templateUrl: './data.component.html',
    styleUrls: ['./data.component.scss'],
    imports: [CommonModule, MaterialModule, VantaShotResultComponent, TranslateModule, ReactiveFormsModule, ButtonComponent, InputComponent, IconComponent]
})
export class DataComponent implements OnInit {
    readonly loader = inject(LoaderService);
  constructor(
    private dataHelperService: DataHelperService,
    private formBuilder: UntypedFormBuilder,
    private dataService: DataService,    
    private toastService: ToastrService,
    private exportService: ExportService,
    private modalService: ModalService,    
  ) {
      this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
      this.treeControl = new FlatTreeControl<ResultNode>(this.getLevel, this.isExpandable),
      this.treeDataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener),
      this.treeDataSource.data = this.testData;

      this.treeControl.expansionModel.changed.subscribe(change => {
        if ((change as SelectionChange<ResultNode>).added
         || (change as SelectionChange<ResultNode>).removed) {
        this.handleTreeControl(change as SelectionChange<ResultNode>);
        }
      });
  }
  public readonly VantaDeviceModelCode: string = 'vanta';
  public firstSearch = true;
  public searching = false;
  public devices: Array<any> = [];
  public searchResult: Array<SearchResultItem>;
  public treeDataSource: MatTreeFlatDataSource<ResultNode, ResultNode>;
  private levels = new Map<ResultNode, number>();
  private treeFlattener: MatTreeFlattener<ResultNode, ResultNode>;

  // FORM
  public treeControl: FlatTreeControl<ResultNode>;
  public searchForm: UntypedFormGroup;
  public startDate: Date;
  public endDate: Date;
  public serialNumbers = [{id: 'all', name: 'All devices'}];
  public selectedResultItems: SearchResultItem[] = [];
  public error: any = { isError: false, errorMessage: '' };
  public isOpen = false;
  public sideBarMode = 'side';
  public monthList: Array<string> = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  public testData: Array<ResultNode> = [	];
  public activeNode: string;
  /** The selection for checklist */
  public checklistSelection = new SelectionModel<ResultNode>(true);
  @ViewChild('filterControls', { static: true }) public filterControls: MatExpansionPanel;
  @ViewChild('shotResult', { static: true }) public shotResult: VantaShotResultComponent;
  
  public hasChild = (_: number, node: ResultNode) => !!node.children && node.children.length > 0;
  public ngOnInit(): void {
    this.isOpen = true;
    this.filterControls.open();
    const startDate = new Date();
    const endDate = new Date();
    startDate.setDate(DateHelper.today().getDate() - 7);
    endDate.setDate(DateHelper.today().getDate());

    this.searchForm = this.formBuilder.group({
      startDate: [startDate, Validators.required],
      endDate: [endDate, Validators.required],
      serialNumbers: new UntypedFormControl([])
    });

    this.dataHelperService.getDevicesByCode(this.VantaDeviceModelCode)
      .subscribe(devices => {
        this.devices = devices;
        this.devices.forEach(device => {
          this.serialNumbers.push({id: device.serialNumber + '', name: device.serialNumber});
        });
       }, error => {
         console.log('Get device failed: ', error);
       });
  }

  public onSubmit(form: any): void {
    this.searchResult = [];
    this.selectedResultItems = [];
    this.checklistSelection.clear();
    const selectedSerialNumbers: Array<string> = this.searchForm.value.serialNumbers;
    const params: ISearchParams = {
      beginDate: form.startDate,
      endDate: form.endDate,
      pageNumber: 0,
      pageSize: 15,
      method: ' ',
      serialNumber: (selectedSerialNumbers.findIndex(itm => itm === 'all') === -1 ? selectedSerialNumbers.toString() : ' ')
    };

    this.firstSearch = false;
    this.searching = true;
    this.filterControls.close();
    this.dataService.doSearchVantaShots(params)
      .subscribe((results: Array<SearchResultItem>) => {
        if ((results || []).length > 0) {
          results.forEach(item => {
            item.checked = false;
            const newStr = item.testedDateUtc.replace('T', ' ').replace(/-/g, '/').split('.')[0];
            item.testedDateUtc = new Date(newStr + ' UTC').toString();
          });
          this.searchResult = results;
        } else {
          this.searchResult = [];
        }
        this.createTree();
        this.searching = false;
      },
      (error) => {
        console.log('Search request failed.', error);
        this.searching = false;
      });
  }

  public createTree() {
    this.testData = [];
    this.searchResult.forEach(itm => {
      const testDate = new Date(itm.testedDateUtc);
      let yearIndex = this.testData.findIndex(year => year.name === testDate.getFullYear() + '');
      if (yearIndex === -1) {
        yearIndex = this.testData.push({id: testDate.getFullYear() + '',
                        name: testDate.getFullYear() + '',
                        children: []}) - 1;
      }
      let monthIndex = this.testData[yearIndex].children.findIndex(month => {
        return month.name === this.monthList[testDate.getMonth()];
      });
      if (monthIndex === -1) {
        monthIndex = this.testData[yearIndex].children.push({
          id: this.testData[yearIndex].name + this.monthList[testDate.getMonth()],
          name: this.monthList[testDate.getMonth()],
          children: [],
          parent: this.testData[yearIndex]
        }) - 1;
      }
      let dayIndex = this.testData[yearIndex].children[monthIndex].children.findIndex(day => {
        return day.name === testDate.getDate() + '';
      });
      if (dayIndex === -1) {
        dayIndex = this.testData[yearIndex].children[monthIndex].children.push({
          id: this.testData[yearIndex].name + this.testData[yearIndex].children[monthIndex].name + testDate.getDate(),
          name: testDate.getDate() + '',
          children: [],
          parent: this.testData[yearIndex].children[monthIndex]}) - 1;
      }
      this.testData[yearIndex].children[monthIndex].children[dayIndex].children.push({
        id: itm.id,
        name: itm.tidName + ' [' + itm.serialNumber + ']',
        value: itm,
        parent: this.testData[yearIndex].children[monthIndex].children[dayIndex]
      });
    }, this);
    this.treeDataSource.data = this.testData;
  }

  public validDateRange() {
    if (this.searchForm.controls.startDate.value === null || this.searchForm.controls.endDate.value === null) {
      this.error = { isError: true, errorMessage: `Date can't be empty`};
    } else if (new Date(this.searchForm.controls.endDate.value) < new Date(this.searchForm.controls.startDate.value)) {
       this.error = { isError: true, errorMessage: `End Date can't be before start date`};
    } else {
      this.error = { isError: false };
    }
  }

  public onSerialNumberSelected(evt: MatSelectionListChange) {
    const list = evt.source;
        
    const selectedValues = list.selectedOptions.selected.map(o => o.value as string);        
    const allId = 'all';
        
    const options = list.options.toArray();
    const deviceIds = options
        .map(o => o.value as string)
        .filter(v => v !== allId);

    const changedValues = new Set(evt.options.map(o => o.value as string));
    const allWasChanged = changedValues.has(allId);

    if (allWasChanged) {
        const allIsNowSelected = selectedValues.includes(allId);

        if (allIsNowSelected) {
            const allIds = this.serialNumbers.map(d => d.id as string); 
            this.searchForm.get('serialNumbers')!.setValue(allIds, { emitEvent: false });
        } else {
            this.searchForm.get('serialNumbers')!.setValue([], { emitEvent: false });
        }

        this.searchForm.get('serialNumbers')!.markAsDirty();
        return;
    }
        
    const selectedDeviceIds = selectedValues.filter(v => v !== allId);
    const allDevicesSelected = selectedDeviceIds.length === deviceIds.length;

    if (allDevicesSelected) {
        const allIds = this.serialNumbers.map(d => d.id as string); 
        this.searchForm.get('serialNumbers')!.setValue(allIds, { emitEvent: false });
    } else {
        this.searchForm.get('serialNumbers')!.setValue(selectedDeviceIds, { emitEvent: false });
    }

    this.searchForm.get('serialNumbers')!.markAsDirty();
  }

  public onSelectResult(resultObject: ResultNode) {
    this.activeNode = resultObject.id;
    this.selectedResultItems = [];
    this.selectedResultItems.push(resultObject.value);
    setTimeout(() => { this.shotResult.updateBlade(0); }, 100);
  }

  public onSelectNoneResultNode(resultObject: ResultNode) {
    this.activeNode = resultObject.id;
    this.selectedResultItems = [];
    this.getChildResultItems(resultObject.children);
    setTimeout(() => { this.shotResult.updateBlade(3); }, 100);
  }

  public getChildResultItems(nodes: Array<ResultNode>): SearchResultItem[] {
    const resultItems = [];
    if (nodes.length > 0) {
      nodes.forEach(itm => {
        if (itm.value) {
          this.selectedResultItems.push(itm.value);
        } else if (itm.children) {
          resultItems.push(this.getChildResultItems(itm.children));
        }
      });
    }
    return resultItems;
  }

   /** Whether all the descendants of the node are selected. */
   public descendantsAllSelected(node: ResultNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.length > 0 && descendants.every(child => {
      return this.checklistSelection.isSelected(child);
    });
    return descAllSelected;
  }

  /** Whether part of the descendants are selected */
  public descendantsPartiallySelected(node: ResultNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some(child => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  /** Toggle the to-do item selection. Select/deselect all the descendants node */
  public itemSelectionToggle(node: ResultNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants);

    // Force update for the parent
    descendants.forEach(child => this.checklistSelection.isSelected(child));
    this.checkAllParentsSelection(node);

  }

  public leafItemSelectionToggle(node: ResultNode): void {
    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
  }

  public get selectedShotsCount(): number {
    return this.checklistSelection.selected.filter((n) => !n.children).length;
  }

  public get devicesSelected(): boolean {
    return this.searchForm.value.serialNumbers.length !== 0;
    }

    public deleteVantaShots(): void {
       
    const shotIds: string[] = this.checklistSelection.selected.filter((n) => !n.children).map((n) => n.id);
    const count = shotIds.length;

    this.modalService
        .open$(ConfirmModalComponent, {
            icon: 'trash-delete',
            title: 'Confirm Action',
            content: `Are you sure you want to delete ${count} test result${count > 1 ? 's' : ''}?`,
            action: {
                label: 'Delete',
                callback$: () => {
                    this.loader.play();

                    return this.dataService.deleteVantaShots(shotIds).pipe(
                        finalize(() => this.loader.stop())
                    );
                }                
            },
            cancelLabel: 'Cancel',
            isError: true
        })
        .subscribe({
            next: () => {                
                this.onSubmit(this.searchForm.value);
            },
            error: () => {
                this.toastService.error(`Couldn't delete the results.`);
            }
        });       
  }
   
  public exportVantaShotCSV(): void {
    const shotIds: string[] = this.checklistSelection.selected.filter((n) => !n.children).map((n) => n.id);
    if (shotIds.length > 5000) {
        this.toastService.info("Can't export more than 5000 results at once.");          
        return;          
    }

    if (shotIds.length > 250) {
        this.toastService.info('Exporting large number of Vanta shots may take some time. Please wait for this process to complete.');
    }
      
    this.loader.play();
    const fileName = 'VantaDataCSV.zip';    
    this.dataService
      .exportVantaShot(shotIds, VantaResultExportType.CsvExport)
      .subscribe(
        (data) => {
          this.exportService.exportVantaShotCSV(fileName, data);
          this.loader.stop();
        },
        (err) => { 
          this.toastService.error(`Couldn't export the result.`);
          this.loader.stop();
        }
      );
  }

  public exportVantaShotJSON() {
    const shotIds: string[] = this.checklistSelection.selected.filter((n) => !n.children).map((n) => n.id);
    if (shotIds.length > 5000) {
        this.toastService.info("Can't export more than 5000 results at once.");
        return;
    }

    if (shotIds.length > 250) {
        this.toastService.info('Exporting large number of Vanta shots may take some time. Please wait for this process to complete.');
    }
     
    this.loader.play();
    const fileName = 'VantaDataJson.zip';
    this.dataService
      .exportVantaShot(shotIds, VantaResultExportType.PrettifiedJson)
      .subscribe(
        (data) => {         
          this.exportService.exportVantaShotsJSON(fileName, data);
          this.loader.stop();
        },
        (err) => {
          this.toastService.error(`Couldn't export the result.`);
          this.loader.stop();
        }
      );
  }

   /* Checks all the parents when a leaf node is selected/unselected */
  private checkAllParentsSelection(node: ResultNode): void {
    let parent: ResultNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  /* Get the parent node of a node */
  private getParentNode(node: ResultNode): ResultNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  /** Check root node checked state and change it accordingly */
  private checkRootNodeSelection(node: ResultNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.length > 0 && descendants.every(child => {
      return this.checklistSelection.isSelected(child);
    });
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  private getLevel = (node: ResultNode): number => {
    return this.levels.get(node) || 0;
  }

  private getChildren = (node: ResultNode) => {
    return node.children;
  }

  private isExpandable = (node: ResultNode): boolean => {
    return !!node.children && node.children.length > 0;
  }

  private transformer = (node: ResultNode, level: number) => {
    this.levels.set(node, level);
    return node;
  }

  private handleTreeControl(change: SelectionChange<ResultNode>) {
    if (change.removed.length > 0 && !!change.removed[0].children) {
      change.removed[0].children.forEach((node) => {
      if (!!node.children && this.treeControl.isExpanded(node)) {
        this.treeControl.collapseDescendants(node);
      }
      });
    }
  }
}
